import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from 'features/components/Button';
import { useLanguage } from 'features/hooks/useLanguage';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { LanguageType } from 'types/enums';
import { z } from 'zod';
import { Input } from '../../component/Input';
import { useGetFormsQuery } from '../../data/useGetFormsQuery/useGetFormsQuery';
import { SelectFormForSurvey } from '../../smart-components/SelectForm';

type SurveyCreationFormData = {
	selectedFormId: string;
	surveyName: string;
};

const createSurveyCreationSchema = (dictionary: (typeof DICTIONARY)[LanguageType.English]) =>
	z.object({
		selectedFormId: z.string().min(1, dictionary.formSelectionRequired),
		surveyName: z.string().trim().min(1, dictionary.surveyNameRequired)
	});

interface SurveyCreationFormProps {
	seriesName: string;
	projectId: string;
	onCancel: () => void;
	onSubmit: (formId: string, surveyName: string) => Promise<void>;
	isLoading?: boolean;
}

export const SurveyCreationForm = ({
	seriesName,
	projectId,
	onCancel,
	onSubmit,
	isLoading = false
}: SurveyCreationFormProps) => {
	const language = useLanguage();
	const dictionary = DICTIONARY[language];

	const {
		register,
		handleSubmit,
		setValue,
		watch,
		formState: { errors }
	} = useForm<SurveyCreationFormData>({
		resolver: zodResolver(createSurveyCreationSchema(dictionary)),
		defaultValues: {
			selectedFormId: '',
			surveyName: ''
		}
	});

	const selectedFormId = watch('selectedFormId');

	const getFormsQuery = useGetFormsQuery({
		projectId,
		seriesName
	});

	const defaultFormId =
		getFormsQuery.data?.activeForms.length === 1
			? getFormsQuery.data.activeForms[0]?.formId.toString()
			: undefined;
	const activeFormId = selectedFormId || defaultFormId;

	useEffect(() => {
		if (defaultFormId && !selectedFormId) {
			setValue('selectedFormId', defaultFormId);
		}
	}, [defaultFormId, selectedFormId, setValue]);

	const onFormSubmit = handleSubmit(async data => {
		await onSubmit(activeFormId || data.selectedFormId, data.surveyName);
	});

	return (
		<div className="flex flex-col gap-4">
			<div>
				<SelectFormForSurvey
					onFormSelected={formId => {
						setValue('selectedFormId', formId);
					}}
					selectedFormId={activeFormId || null}
					seriesName={seriesName}
				/>
				{errors.selectedFormId && (
					<div className="flex justify-center mt-2">
						<p className="text-warning-800 font-bold text-base">
							{errors.selectedFormId.message}
						</p>
					</div>
				)}
			</div>

			<Input
				label={dictionary.surveyName}
				id="survey-name"
				{...register('surveyName')}
				description={dictionary.surveyNameDescription}
				error={errors.surveyName?.message}
			/>

			<div className="flex justify-end mt-10 gap-2">
				<Button text={dictionary.cancel} onClick={onCancel} variant="secondary" />
				<Button
					text={dictionary.create}
					onClick={onFormSubmit}
					variant="primary"
					loading={isLoading}
				/>
			</div>
		</div>
	);
};

const DICTIONARY = {
	[LanguageType.English]: {
		surveyName: 'Survey name',
		surveyNameDescription:
			'A name that identifies the survey.\n\nSuch as "Baseline", "3 month" and "6 month" etc.',
		surveyNameRequired: 'Survey name is required',
		formSelectionRequired: 'Please select a form',
		create: 'Create',
		cancel: 'Cancel'
	},
	[LanguageType.Norwegian]: {
		surveyName: 'Undersøkelsesnavn',
		surveyNameDescription:
			'Et navn som identifiserer undersøkelsen.\n\nSom "Inklusjon", "3. måned" og "6. måned" osv.',
		surveyNameRequired: 'Undersøkelsesnavn er påkrevd',
		formSelectionRequired: 'Velg et skjema',
		create: 'Lag',
		cancel: 'Avbryt'
	}
};
